import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { LogoWall } from '../components/LogoWall';
import { ClosingCTA } from '../components/ClosingCTA';
import { EnterpriseUseCases } from '../components/EnterpriseUseCases';
import { EnterpriseIndustries } from '../components/EnterpriseIndustries';
import { EnterpriseConnections } from '../components/EnterpriseConnections';
import { EnterpriseBrain } from '../components/EnterpriseBrain';
import { ShieldCheck, Lock, FileKey, Database, Users, Activity, Headset, BookOpen, Zap } from 'lucide-react';
const SECURITY_FEATURES = [{
  title: 'SOC 2 Type II',
  description: 'Independently audited and certified for security, availability, and confidentiality.',
  icon: ShieldCheck
}, {
  title: 'HIPAA Compliant',
  description: 'Ready for healthcare. We sign BAAs and ensure PHI is handled with strict compliance.',
  icon: Activity
}, {
  title: 'GDPR Ready',
  description: 'Full compliance with EU data protection regulations, including data residency options.',
  icon: Lock
}, {
  title: 'SSO & SAML',
  description: 'Integrate with Okta, Google Workspace, Azure AD, and other major identity providers.',
  icon: FileKey
}, {
  title: 'Audit Logs',
  description: 'Comprehensive logging of all workspace activity, exports, and administrative changes.',
  icon: Database
}, {
  title: 'Zero Model Training',
  description: 'Your data is yours. We never use enterprise customer data to train our AI models.',
  icon: Users
}];
const SUPPORT_FEATURES = [{
  title: 'Dedicated Success Manager',
  description: 'A dedicated partner to help you map workflows, train your team, and drive adoption.',
  icon: Users
}, {
  title: 'Custom Onboarding',
  description: 'Tailored rollout plans, white-glove setup, and live training sessions for your org.',
  icon: BookOpen
}, {
  title: '24/7 Priority Support',
  description: 'Skip the queue. Get guaranteed SLAs and direct access to our engineering team.',
  icon: Headset
}];
export function Enterprise() {
  return <div className="min-h-screen bg-white font-body selection:bg-[#EA580C]/20 selection:text-[#333333]" data-id="element-8461" data-mp="2xjzaogef0hhk">
      {/* Hero Section */}
      <section className="relative w-full bg-[#FAFAFA] pt-40 pb-32 md:pb-48 overflow-hidden flex flex-col items-center justify-center" data-id="element-8462" data-mp="2njyp0nt2ppa7">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative z-10 text-center" data-id="element-8463" data-mp="2sjl30zgq79k2">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col items-center" data-id="element-8464" data-mp="3g9ff3et80u39">
            {/* Eyebrow Pill */}
            <div className="inline-flex items-center gap-2 bg-[#FFEDD5]/70 border border-[#EA580C]/20 px-4 py-1.5 rounded-full mb-8" data-id="element-8465" data-mp="3bhe55zjizfge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-8466" data-mp="30t1my9nxeko">
                <path d="M12 2L15 9L22 12L15 15L12 22L9 15L2 12L9 9L12 2Z" data-id="element-8467" data-mp="1sig79ai4a9of" />
              </svg>
              <span className="text-sm font-medium text-[#333333]" data-id="element-8468" data-mp="1nk6et7ns1n6i">
                <span className="font-bold" data-id="element-8469" data-mp="1nhsg8otw1su7">Giga</span> Enterprise
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-title text-5xl md:text-7xl lg:text-[80px] font-bold tracking-tight text-[#333333] mb-8 leading-[1.05] max-w-5xl mx-auto" data-id="element-8470" data-mp="1x96dsr2a4ikp">
              Give everyone on your team a shared brain.
            </h1>

            {/* Subhead (Gray text) */}
            <p className="text-xl md:text-2xl text-[#475569] mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-8471" data-mp="14ndhn0bho26w">
              One AI brain that connects to everything, works for every
              department, and meets the security and governance bar your
              organization demands.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-8472" data-mp="2pywt33fndqie">
              <Link to="/contact" className="inline-flex items-center justify-center bg-[#EA580C] hover:bg-[#C2410C] text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg shadow-[#EA580C]/20" data-id="element-8473" data-mp="3l8tllm08hhfn">
                Contact Sales
              </Link>
              <Link to="/unlisted/security" className="inline-flex items-center gap-1.5 text-[#333333] font-semibold px-4 py-4 hover:text-[#EA580C] transition-colors" data-id="element-8474" data-mp="3ubb9e4vjei4r">
                View security docs
                <span aria-hidden="true" data-id="element-8475" data-mp="7zxijizjlwrw">→</span>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* SaaS-y Decorative Bottom Elements (Faint Cards) */}
        <div className="absolute bottom-0 left-0 w-full h-32 pointer-events-none overflow-hidden flex items-end justify-center gap-4 opacity-40" data-id="element-8476" data-mp="gv3tewdo751a">
          {/* Fade mask so they blend smoothly into the background */}
          <div className="absolute inset-0 bg-gradient-to-t from-transparent to-[#FAFAFA] z-10" data-id="element-8477" data-mp="qqosalnfzp6t" />

          {/* Row of faint decorative cards */}
          {[...Array(12)].map((_, i) => <div key={i} className="w-32 h-24 bg-white border border-neutral-200 rounded-t-2xl shadow-sm shrink-0 relative overflow-hidden" data-id="element-8478" data-mp="2gayd9jp1443c" data-mpi={i}>
              {/* Subtle diagonal lines inside the cards for extra texture */}
              <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 0, transparent 50%)',
            backgroundSize: '10px 10px'
          }} data-id="element-8479" data-mp="26dagypbryoj2" />
            </div>)}
        </div>
      </section>

      {/* Logo Wall */}
      <LogoWall data-id="element-8480" data-mp="2pabpul8g98sa" />

      {/* Built for every team — org-wide use-case explorer */}
      <EnterpriseUseCases data-id="element-8481" data-mp="2p88o53xxpr7q" />

      {/* Supports every industry — editorial vertical band */}
      <EnterpriseIndustries data-id="element-8482" data-mp="2k95vkiz55dgn" />

      {/* Connects to everything — system connections */}
      <EnterpriseConnections data-id="element-8483" data-mp="3hrxhyh6h5gnd" />

      {/* GigaBrain core — the engine + memory */}
      <EnterpriseBrain data-id="element-8484" data-mp="3cz4dzvityccc" />

      {/* Security & Compliance Grid */}
      <section id="security" className="w-full bg-[#FAF8F4] py-28 md:py-40 border-b border-neutral-200 scroll-mt-24" data-id="element-8485" data-mp="1b1u6jjmlluo9">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-8486" data-mp="1g0f5lfq468b4">
          <div className="text-center mb-16 md:mb-20" data-id="element-8487" data-mp="11ckge9bbem2s">
            <span className="text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 block" data-id="element-8488" data-mp="11c15hck30xy9">
              Security First
            </span>
            <h2 className="text-4xl md:text-5xl font-title font-bold text-[#333333] mb-6 tracking-tight max-w-3xl md:max-w-4xl mx-auto" data-id="element-8489" data-mp="sbcruiylozlw">
              Bank-grade security, by default.
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto" data-id="element-8490" data-mp="ie82695jqm0c">
              We built Giga from the ground up to protect your most sensitive
              company data. Your context stays yours.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-8491" data-mp="2ct0u9y780unj">
            {SECURITY_FEATURES.map((feature, i) => {
            const Icon = feature.icon;
            return <motion.div key={feature.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white rounded-2xl p-6 md:p-8 border border-neutral-200 shadow-sm flex flex-col" data-id="element-8492" data-mp="64icmo1u4nl8" data-mpi={i}>
                  <div className="w-12 h-12 rounded-xl bg-[#FFEDD5] text-[#EA580C] flex items-center justify-center mb-6" data-id="element-8493" data-mp="ow1hz1jc0dqf">
                    <Icon size={24} data-id="element-8494" data-mp="ys4glxez5u8m" />
                  </div>
                  <h3 className="text-xl font-bold text-[#333333] mb-3" data-id="element-8495" data-mp="yrmfamdggptu">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-600 leading-relaxed" data-id="element-8496" data-mp="30jhlwnkuu457">
                    {feature.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Governance & Control (Side by Side) */}
      <section className="w-full bg-white py-28 md:py-40 border-b border-neutral-200 overflow-hidden" data-id="element-8497" data-mp="29qpjzrfg29r5">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-8498" data-mp="2eohbwlg39vvc">
          <div className="flex flex-col lg:flex-row items-center gap-16 md:gap-24" data-id="element-8499" data-mp="2jgsclqinlloy">
            {/* Text Content */}
            <div className="flex-1 max-w-xl" data-id="element-8500" data-mp="2oetvaac63izw">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} data-id="element-8501" data-mp="1qscz58f67bfq">
                <span className="text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 block" data-id="element-8502" data-mp="20sneaykrmsxj">
                  Admin Controls
                </span>
                <h2 className="text-4xl md:text-5xl font-title font-bold text-[#333333] mb-6 leading-[1.1] tracking-tight" data-id="element-8503" data-mp="3c4xyiglg1gqd">
                  Total control over your workspace.
                </h2>
                <p className="text-lg text-neutral-600 leading-relaxed mb-8" data-id="element-8504" data-mp="14io3dx2ihyqx">
                  Manage how your team uses Giga with granular permissions,
                  custom data retention policies, and deep visibility into
                  usage.
                </p>
                <ul className="space-y-5" data-id="element-8505" data-mp="qt2oaxru23o6">
                  {['Role-Based Access Control (RBAC)', 'Custom Data Retention Policies', 'Workspace Usage Analytics', 'Domain Capture & Auto-Provisioning'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-8506" data-mp="gtvc4dwu4f2n" data-mpi={i}>
                      <div className="w-6 h-6 rounded-full bg-[#FFEDD5] text-[#EA580C] flex items-center justify-center shrink-0" data-id="element-8507" data-mp="csrze0a32z5n">
                        <Zap size={14} data-id="element-8508" data-mp="7ugpxqnpaqny" />
                      </div>
                      <span className="text-[#333333] font-medium" data-id="element-8509" data-mp="276swafm2pc33">{item}</span>
                    </li>)}
                </ul>
              </motion.div>
            </div>

            {/* Abstract UI Mockup */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="flex-1 w-full" data-id="element-8510" data-mp="3ckeja2h2bw8m">
              <div className="w-full aspect-[4/3] rounded-3xl bg-[#FAF8F4] border border-neutral-200 p-8 shadow-inner flex flex-col gap-4" data-id="element-8511" data-mp="3gm8evwceia1x">
                {/* Mockup Header */}
                <div className="flex items-center justify-between border-b border-neutral-200 pb-4 mb-2" data-id="element-8512" data-mp="2sukttv1tlt7k">
                  <div className="flex items-center gap-3" data-id="element-8513" data-mp="2ot94lqmuz9zw">
                    <div className="w-8 h-8 rounded bg-[#2B211A] flex items-center justify-center" data-id="element-8514" data-mp="32n8q8oow1v5r">
                      <ShieldCheck size={16} className="text-white" data-id="element-8515" data-mp="2xu7ayfkrrtln" />
                    </div>
                    <div className="font-bold text-[#333333]" data-id="element-8516" data-mp="2a4v25gtctnjv">
                      Admin Console
                    </div>
                  </div>
                  <div className="w-24 h-8 rounded-full bg-neutral-200 animate-pulse" data-id="element-8517" data-mp="kp2au01o1mb8" />
                </div>
                {/* Mockup Rows */}
                {[1, 2, 3].map(row => <div key={row} className="bg-white rounded-xl p-4 border border-neutral-100 shadow-sm flex items-center justify-between" data-id="element-8518" data-mp="ph25pe2vpp0y">
                    <div className="flex items-center gap-4" data-id="element-8519" data-mp="gn77yr4370vc">
                      <div className="w-10 h-10 rounded-full bg-neutral-100 animate-pulse" data-id="element-8520" data-mp="glcntf0n3csb" />
                      <div className="space-y-2" data-id="element-8521" data-mp="14dt4pm8n4yq1">
                        <div className="w-32 h-3 bg-neutral-200 rounded-full animate-pulse" data-id="element-8522" data-mp="18f4ym3yjs641" />
                        <div className="w-20 h-2 bg-neutral-100 rounded-full animate-pulse" data-id="element-8523" data-mp="ufdedlhftthl" />
                      </div>
                    </div>
                    <div className="w-16 h-6 rounded-full bg-[#FFEDD5] border border-[#EA580C]/20" data-id="element-8524" data-mp="zcdnk59l3ccx" />
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dedicated Support */}
      <section className="w-full bg-[#FAF8F4] py-28 md:py-40" data-id="element-8525" data-mp="301tb68utrtlm">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-8526" data-mp="3019pch220l2b">
          <div className="text-center mb-16 md:mb-20" data-id="element-8527" data-mp="2w1isbvlxp8pc">
            <span className="text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 block" data-id="element-8528" data-mp="2w2tz84aoewko">
              Enterprise Support
            </span>
            <h2 className="text-4xl md:text-5xl font-title font-bold text-[#333333] mb-6 tracking-tight max-w-3xl md:max-w-4xl mx-auto" data-id="element-8529" data-mp="2m9v4s5si0bt5">
              A true partner, not just a vendor.
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto" data-id="element-8530" data-mp="2caxb6og7ts3a">
              We work closely with our enterprise customers to ensure successful
              rollouts and maximum ROI.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-8531" data-mp="23dvfj2qwnmd9">
            {SUPPORT_FEATURES.map((feature, i) => {
            const Icon = feature.icon;
            return <motion.div key={feature.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white rounded-2xl p-6 md:p-8 border border-neutral-200 shadow-sm text-center flex flex-col items-center" data-id="element-8532" data-mp="1kyt6jrjoxdu" data-mpi={i}>
                  <div className="w-16 h-16 rounded-full bg-[#FAF8F4] border border-neutral-100 text-[#333333] flex items-center justify-center mb-6" data-id="element-8533" data-mp="3maqcwdtjbgm9">
                    <Icon size={28} data-id="element-8534" data-mp="3r9tgruxs3gsn" />
                  </div>
                  <h3 className="text-xl font-bold text-[#333333] mb-3" data-id="element-8535" data-mp="30fkr2zjho4">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-600 leading-relaxed" data-id="element-8536" data-mp="46ewrl2nu435">
                    {feature.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <ClosingCTA data-id="element-8537" data-mp="1e2v97xw8ml96" />
    </div>;
}